/**
 * BubbleActions — revela as ações do balão SOB DEMANDA, sem reservar layout.
 *
 * Padrão WhatsApp:
 * - Desktop (isMobile=false): as ações aparecem no HOVER do wrapper.
 * - Mobile (isMobile=true): long-press (~400ms) via pointer events abre o menu;
 *   o timer é cancelado se houver deslocamento > 10px desde o pointer-down
 *   (evita disparar durante scroll/swipe-to-reply) ou ao soltar/cancelar.
 *
 * Em REPOUSO o wrapper renderiza apenas `children` — nenhum ícone/elemento
 * ocupa largura fixa no fluxo. O menu (Mantine Popover) só é montado quando
 * aberto, ancorado acima do balão (position="top", withArrow, withinPortal).
 *
 * O conteúdo do dropdown mostra uma barra de reações rápidas INLINE (mesmos
 * emojis rápidos do EmojiPicker) e uma lista de ações: Responder (se onReply),
 * Copiar, Copiar JSON (se onCopyJson) e `extraItems` (ex.: apagar/ocultar que o
 * balão já monta).
 *
 * O menu permanece aberto enquanto o ponteiro estiver sobre o Target OU sobre o
 * Dropdown — o fechamento é adiado (~120ms) para tolerar o gap físico do arrow
 * entre o balão e o dropdown (evita fechar ao mover o mouse pra clicar).
 */
import { useEffect, useRef, useState } from 'react'
import { Box, Divider, Group, Popover, Text, UnstyledButton } from '@mantine/core'
import { IconArrowBackUp, IconCopy, IconCode } from '@tabler/icons-react'

// Conjunto rápido de reações — mesmo do EmojiPicker (constante QUICK, não exportada).
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏']

// Atraso ao fechar o menu no desktop, para tolerar o gap balão↔dropdown (ms).
const HOVER_CLOSE_DELAY_MS = 120

const actionItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '6px 10px',
  borderRadius: 6,
  fontSize: 14,
  color: 'var(--mantine-color-text)',
}

const quickReactionStyle: React.CSSProperties = {
  fontSize: 20,
  lineHeight: 1,
  padding: '4px 6px',
  borderRadius: 6,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  transition: 'background 0.1s ease',
}

// Duração do long-press no mobile (ms).
const LONG_PRESS_MS = 400
// Deslocamento máximo tolerado antes de cancelar o long-press (px).
const MOVE_CANCEL_THRESHOLD = 10

interface BubbleActionsProps {
  isMobile: boolean
  onReact: (emoji: string) => void
  onReply?: () => void
  onCopy: () => void
  onCopyJson?: () => void
  extraItems?: React.ReactNode
  children: React.ReactNode
}

export function BubbleActions({
  isMobile,
  onReact,
  onReply,
  onCopy,
  onCopyJson,
  extraItems,
  children,
}: BubbleActionsProps) {
  const [opened, setOpened] = useState(false)

  // Timer do long-press + posição inicial do pointer para detectar deslocamento.
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pressStart = useRef<{ x: number; y: number } | null>(null)

  // Timer para o fechamento adiado no hover do desktop.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function clearPressTimer() {
    if (pressTimer.current !== null) {
      clearTimeout(pressTimer.current)
      pressTimer.current = null
    }
    pressStart.current = null
  }

  function clearCloseTimer() {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  // Limpa os timers no unmount.
  useEffect(() => {
    return () => {
      clearPressTimer()
      clearCloseTimer()
    }
  }, [])

  function open() {
    clearCloseTimer()
    setOpened(true)
  }

  function close() {
    clearCloseTimer()
    setOpened(false)
  }

  // ── Desktop: hover ─────────────────────────────────────────────────────────
  // Abrir cancela qualquer fechamento pendente; entrar no Target OU no Dropdown
  // mantém aberto. Sair de qualquer um agenda o fechamento (cancelável).
  function handleMouseEnter() {
    if (isMobile) return
    open()
  }

  function scheduleClose() {
    if (isMobile) return
    clearCloseTimer()
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null
      setOpened(false)
    }, HOVER_CLOSE_DELAY_MS)
  }

  // ── Mobile: long-press via pointer events ───────────────────────────────────
  function handlePointerDown(e: React.PointerEvent) {
    if (!isMobile) return
    pressStart.current = { x: e.clientX, y: e.clientY }
    clearTimeoutOnly()
    pressTimer.current = setTimeout(() => {
      pressTimer.current = null
      open()
    }, LONG_PRESS_MS)
  }

  // Cancela apenas o timeout (mantém pressStart para leituras subsequentes de move).
  function clearTimeoutOnly() {
    if (pressTimer.current !== null) {
      clearTimeout(pressTimer.current)
      pressTimer.current = null
    }
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!isMobile || pressStart.current === null) return
    const dx = Math.abs(e.clientX - pressStart.current.x)
    const dy = Math.abs(e.clientY - pressStart.current.y)
    if (dx > MOVE_CANCEL_THRESHOLD || dy > MOVE_CANCEL_THRESHOLD) {
      clearPressTimer()
    }
  }

  function handlePointerEnd() {
    if (!isMobile) return
    clearPressTimer()
  }

  // ── A11y: abrir via teclado ─────────────────────────────────────────────────
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === 'ContextMenu') {
      e.preventDefault()
      open()
    } else if (e.key === 'Escape' && opened) {
      close()
    }
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="top"
      withArrow
      withinPortal
      shadow="md"
      trapFocus={false}
      closeOnClickOutside
    >
      <Popover.Target>
        <Box
          role="group"
          aria-label="Ações da mensagem"
          tabIndex={0}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={scheduleClose}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          onPointerLeave={handlePointerEnd}
          onKeyDown={handleKeyDown}
          // width fit-content: o alvo de hover cobre SÓ o balão, não a linha
          // inteira — assim o menu só abre com o mouse sobre a mensagem em si
          // (antes, o espaço vazio ao lado do balão também abria).
          style={{ outline: 'none', width: 'fit-content', maxWidth: '100%' }}
        >
          {children}
        </Box>
      </Popover.Target>

      <Popover.Dropdown p={4}>
        {/* Envolve o conteúdo para manter o menu aberto enquanto o mouse
            estiver sobre o dropdown (tolera o gap do arrow). */}
        <Box onMouseEnter={handleMouseEnter} onMouseLeave={scheduleClose}>
          {/* Barra de reações rápidas INLINE — flui no topo, sem overlay. */}
          <Group gap="xs" wrap="nowrap" px={6} py={2}>
            {QUICK_REACTIONS.map((emoji) => (
              <UnstyledButton
                key={emoji}
                onClick={() => {
                  onReact(emoji)
                  close()
                }}
                aria-label={`Reagir ${emoji}`}
                style={quickReactionStyle}
                onMouseEnter={(ev) => {
                  ;(ev.currentTarget as HTMLButtonElement).style.background =
                    'var(--mantine-color-default-hover)'
                }}
                onMouseLeave={(ev) => {
                  ;(ev.currentTarget as HTMLButtonElement).style.background = 'transparent'
                }}
              >
                {emoji}
              </UnstyledButton>
            ))}
          </Group>

          <Divider my={4} />

          {/* Ações */}
          <Box>
            {onReply && (
              <ActionRow
                icon={<IconArrowBackUp size={14} />}
                label="Responder"
                onClick={() => {
                  onReply()
                  close()
                }}
              />
            )}
            <ActionRow
              icon={<IconCopy size={14} />}
              label="Copiar"
              onClick={() => {
                onCopy()
                close()
              }}
            />
            {onCopyJson && (
              <ActionRow
                icon={<IconCode size={14} />}
                label="Copiar JSON"
                onClick={() => {
                  onCopyJson()
                  close()
                }}
              />
            )}
            {extraItems}
          </Box>
        </Box>
      </Popover.Dropdown>
    </Popover>
  )
}

/** Linha de ação do menu (botão com ícone + rótulo). */
function ActionRow({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={actionItemStyle}
      onMouseEnter={(ev) => {
        ;(ev.currentTarget as HTMLButtonElement).style.background =
          'var(--mantine-color-default-hover)'
      }}
      onMouseLeave={(ev) => {
        ;(ev.currentTarget as HTMLButtonElement).style.background = 'transparent'
      }}
    >
      <Group gap={8} wrap="nowrap">
        {icon}
        <Text size="sm">{label}</Text>
      </Group>
    </UnstyledButton>
  )
}
